import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Camera } from '@/models/camera';
import { Project } from '@/models/project';
import BatterieLevelComponent from '@/components/batterielevel/batterielevel.component.vue';
import { weatherService } from '@/services/';
import { State, Getter } from 'vuex-class';
import { ForecastResult, WeatherResult } from '@/services/weather/weather.interfaces';
import { Cache, Storage, Logger } from 'aws-amplify';
import { addHours } from 'date-fns';
import * as Plotly from 'plotly.js-dist';
import { fetchWeatherApi } from 'openmeteo';
import { telemetryService } from '@/services';
import { TelemetryItemDto } from '@/services/cameras/telemetry.interfaces';

const logger = new Logger('Data');

type WeatherMetricKey =
  | 'temperature2m'
  | 'relativeHumidity2m'
  | 'rain'
  | 'snowfall'
  | 'surfacePressure'
  | 'windSpeed10m';

type WeatherPlotType = 'line' | 'bar';

interface WeatherMetricMeta {
  key: WeatherMetricKey;
  short: string;
  label: string;
  unit: string;
  icon: string;
  plotType: WeatherPlotType;
}

type HalfDayKey = 'am' | 'pm';

interface DailyDisruption {
  day: string;
  rain: number;
  snow: number;
  wind: number;
  freeze: number;
  total: number;
}

interface MonthlyDisruption {
  month: string;
  rain: number;
  snow: number;
  wind: number;
  freeze: number;
  total: number;
}

@Component({
  components: { BatterieLevelComponent },
})
export default class Data extends Vue {
  @Prop({ type: Object, required: true }) private readonly camera!: Camera; // peut venir de project.cameras (stale)
  @Prop({ type: Object, required: true }) private project!: Project;

  @State('locale', { namespace: 'profile' }) private readonly locale!: string;

  @Getter('cameraWithId', { namespace: 'camera' })
  private readonly cameraWithId!: (id?: string) => Camera | undefined;

  private visible = false;
  private isDownloadingPDF = false;

  private weather: WeatherResult | null = null;
  private forecast: ForecastResult | null = null;

  private showBatteryGraph: boolean | null = true;
  private openMeteoData: any = null;

  // -------------------------
  // Météo — choix de métrique
  // -------------------------
  private selectedMetric: WeatherMetricKey = 'temperature2m';

  private weatherMetrics: WeatherMetricMeta[] = [
    {
      key: 'temperature2m',
      short: 'Temp',
      label: 'Température',
      unit: '°C',
      icon: 'fa-solid fa-temperature-half',
      plotType: 'line',
    },
    { key: 'windSpeed10m', short: 'Vent', label: 'Vent', unit: 'm/s', icon: 'fa-solid fa-wind', plotType: 'line' },
    {
      key: 'surfacePressure',
      short: 'Press',
      label: 'Pression',
      unit: 'hPa',
      icon: 'fa-solid fa-gauge-high',
      plotType: 'line',
    },
    {
      key: 'relativeHumidity2m',
      short: 'Hum',
      label: 'Humidité',
      unit: '%',
      icon: 'fa-solid fa-droplet',
      plotType: 'line',
    },
    { key: 'rain', short: 'Pluie', label: 'Pluie', unit: 'mm/h', icon: 'fa-solid fa-cloud-rain', plotType: 'bar' },
    { key: 'snowfall', short: 'Neige', label: 'Neige', unit: 'mm/h', icon: 'fa-solid fa-snowflake', plotType: 'bar' },
  ];

  private plotlyWeatherReady = { history: false, forecast: false };

  private weatherDisruptions: DailyDisruption[] = [];
  private monthlyDisruptions: MonthlyDisruption[] = [];

  private isWorkingHour(d: Date): boolean {
    // À ajuster selon vos horaires chantier
    // Ici : 8h-12h et 13h-17h, du lundi au vendredi
    const day = d.getDay(); // 0=dimanche, 6=samedi
    if (day === 0 || day === 6) return false;

    const hour = d.getHours();
    return [7, 8, 9, 10, 11, 13, 14, 15, 16].includes(hour);
  }

  private readonly LEGAL_OPENING_HOUR = 8;

private isHoliday(date: Date): boolean {
  // À brancher sur une vraie source FR.
  // Version minimale : liste fournie par backend / config.
  const key = this.dayKey(date);
  return this.frHolidays.has(key);
}

  private frHolidays = new Set<string>([
    // Exemples 2026 à compléter / alimenter automatiquement
    '2026-01-01',
    '2026-04-06',
    '2026-05-01',
    '2026-05-08',
    '2026-05-14',
    '2026-05-25',
    '2026-07-14',
    '2026-08-15',
    '2026-11-01',
    '2026-11-11',
    '2026-12-25',
  ]);

  private isExcludedDay(date: Date): boolean {
    return date.getDay() === 0 || this.isHoliday(date);
  }

  private dayKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  private getHalfDay(date: Date): HalfDayKey | null {
    const h = date.getHours();
    if (h >= 8 && h < 13) return 'am';
    if (h >= 13 && h < 18) return 'pm';
    return null;
  }

  private telemetryItems: TelemetryItemDto[] = [];
  private telemetryLoading = false;
  private telemetryError: string | null = null;

  /** ✅ Source unique: on récupère la caméra “fraîche” depuis le store. */
  private get cameraFresh(): Camera {
    return this.cameraWithId(this.camera?.id) ?? this.camera;
  }

  private get hasExternalBattery(): boolean {
    return this.externalBatteryLastLevel !== null;
  }

  private async loadTelemetry(): Promise<void> {
    if (!this.cameraFresh?.id) return;
    this.telemetryLoading = true;
    this.telemetryError = null;

    try {
      // 10 jours par défaut (à ajuster)
      const to = Math.floor(Date.now() / 1000);
      const from = to - 10 * 24 * 3600;

      const result = await telemetryService.getCameraTelemetry(this.cameraFresh.id, { from, to, limit: 2000 });
      this.telemetryItems = (result.items ?? [])
        .slice()
        .sort((a: TelemetryItemDto, b: TelemetryItemDto) => a.ts - b.ts);
    } catch (e: any) {
      this.telemetryError = e?.message ?? String(e);
      this.telemetryItems = [];
    } finally {
      this.telemetryLoading = false;
    }
  }

  // -------------------------
  // Helpers date / parsing
  // -------------------------

  /** Convertit ISO / Date / epoch sec / epoch ms -> epoch ms */
  private toEpochMs(d: any): number | null {
    if (d instanceof Date) return d.getTime();

    const n = Number(d);
    if (Number.isFinite(n)) {
      return n < 1e12 ? n * 1000 : n; // sec->ms
    }

    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? null : dt.getTime();
  }

  private formatLocalDateTime(d: Date): string {
    // format simple sans dépendance supplémentaire
    const pad = (x: number) => String(x).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(
      d.getMinutes()
    )}`;
  }

  // -------------------------
  // Batterie interne (shadow)
  // -------------------------

  private get internalBatteryLevel(): number | null {
    const v = (this.cameraFresh as any)?.shadow?.reported?.Battery_Percent;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  private batteryUi(level: number | null): { icon: string; colorClass: string } {
    if (level === null) return { icon: 'fa-solid fa-battery-slash', colorClass: 'text-muted' };

    const icon =
      level <= 10
        ? 'fa-solid fa-battery-empty'
        : level <= 25
        ? 'fa-solid fa-battery-quarter'
        : level <= 50
        ? 'fa-solid fa-battery-half'
        : level <= 75
        ? 'fa-solid fa-battery-three-quarters'
        : 'fa-solid fa-battery-full';

    const colorClass =
      level <= 10 ? 'text-danger' : level < 50 ? 'text-warning' : level < 75 ? 'text-primary' : 'text-batt-good';

    return { icon, colorClass };
  }

  // -------------------------
  // Batterie externe (séries)
  // -------------------------

  private get externalBatteryLevelsNormalized(): Array<{ dateMs: number; level: number }> {
    const raw = (this.cameraFresh as any)?.externalBatteryLevels;
    if (!Array.isArray(raw)) return [];

    return raw
      .map((x: any) => ({
        dateMs: this.toEpochMs(x?.date),
        level: Number(x?.level ?? x?.value ?? x?.percentage),
      }))
      .filter((x: any) => x.dateMs != null && Number.isFinite(x.level)) as any;
  }

  private get externalBatteryLast(): { dateMs: number; level: number } | null {
    const arr = this.externalBatteryLevelsNormalized;
    if (!arr.length) return null;
    return arr.reduce((best, cur) => (cur.dateMs > best.dateMs ? cur : best));
  }

  private get externalBatteryLastAtLabel(): string | null {
    const last = this.externalBatteryLast;
    if (!last) return null;
    return this.formatLocalDateTime(new Date(last.dateMs));
  }

  private get externalBatteryUi() {
    return this.batteryUi(this.externalBatteryLastLevel);
  }

  // -------------------------
  // Température device (shadow)
  // -------------------------

  /** Couleur basée sur °C (pas sur l’unité brute). */
  private getTemperatureColor(tempValue: any): string {
    if (
      tempValue === null ||
      tempValue === undefined ||
      tempValue === '' ||
      tempValue === '--' ||
      tempValue === 'N/A'
    ) {
      return 'text-temp-secondary';
    }

    const temp = Number(tempValue);
    if (!Number.isFinite(temp)) return 'text-temp-secondary';

    if (temp > 50) return 'text-temp-red';
    if (temp > 40) return 'text-temp-orange';
    if (temp > 30) return 'text-temp-yellow';
    if (temp > 10) return 'text-temp-green';
    if (temp > 0) return 'text-temp-lightblue';
    return 'text-temp-blue';
  }

  // -------------------------
  // Weather UI
  // -------------------------

  private get temperatureUnit(): 'metric' | 'imperial' {
    return this.locale === 'en' ? 'imperial' : 'metric';
  }

  private get weatherOfDayPicto(): string {
    return this.getPictoOf(this.weather);
  }

  private get selectedMetricMeta(): WeatherMetricMeta {
    return this.weatherMetrics.find((m) => m.key === this.selectedMetric) ?? this.weatherMetrics[0];
  }

  private get selectedMetricLabel(): string {
    const m = this.selectedMetricMeta;
    return `${m.label} (${m.unit})`;
  }

  // -------------------------
  // Plotly weather
  // -------------------------

  private handleOpenMeteoResponse(response: any) {
    const utcOffsetSeconds = response.utcOffsetSeconds();
    const hourly = response.hourly()!;

    const range = (start: number, stop: number, step: number) =>
      Array.from({ length: Math.floor((stop - start) / step) }, (_, i) => start + i * step);

    this.openMeteoData = {
      hourly: {
        time: range(Number(hourly.time()), Number(hourly.timeEnd()), hourly.interval()).map(
          (t) => new Date((t + utcOffsetSeconds) * 1000)
        ),
        temperature2m: hourly.variables(0)!.valuesArray()!,
        relativeHumidity2m: hourly.variables(1)!.valuesArray()!,
        rain: hourly.variables(2)!.valuesArray()!,
        snowfall: hourly.variables(3)!.valuesArray()!,
        surfacePressure: hourly.variables(4)!.valuesArray()!,
        windSpeed10m: hourly.variables(5)!.valuesArray()!,
      },
    };

    // ✅ RE-CALCUL ICI
    this.weatherDisruptions = this.calculateWeatherDisruptions();
    this.monthlyDisruptions = this.calculateMonthlyDisruptions();

    this.$nextTick(() => {
      if (this.visible) this.renderWeatherCharts();
    });
  }

  private calculateWeatherDisruptions(): DailyDisruption[] {
    if (!this.openMeteoData?.hourly) return [];

    const times: Date[] = this.openMeteoData.hourly.time ?? [];
    const rain: number[] = Array.from(this.openMeteoData.hourly.rain ?? []);
    const snow: number[] = Array.from(this.openMeteoData.hourly.snowfall ?? []);
    const windGust: number[] = Array.from(this.openMeteoData.hourly.windGust10m ?? []);
    const temp: number[] = Array.from(this.openMeteoData.hourly.temperature2m ?? []);

    const nowMs = Date.now();

    const byDay = new Map<string, {
      day: string;
      am: { rain: number[]; snow: number[]; gust: number[]; temp: Array<{ hour: number; value: number }> };
      pm: { rain: number[]; snow: number[]; gust: number[]; temp: Array<{ hour: number; value: number }> };
    }>();

    for (let i = 0; i < times.length; i++) {
      const d = times[i];
      if (!(d instanceof Date) || Number.isNaN(d.getTime())) continue;
      if (d.getTime() > nowMs) continue;
      if (this.isExcludedDay(d)) continue;

      const half = this.getHalfDay(d);
      if (!half) continue;

      const key = this.dayKey(d);
      const bucket = byDay.get(key) ?? {
        day: key,
        am: { rain: [], snow: [], gust: [], temp: [] },
        pm: { rain: [], snow: [], gust: [], temp: [] },
      };

      bucket[half].rain.push(Number(rain[i]) || 0);
      bucket[half].snow.push(Number(snow[i]) || 0);
      bucket[half].gust.push(Number(windGust[i]) || 0);
      bucket[half].temp.push({ hour: d.getHours(), value: Number(temp[i]) });

      byDay.set(key, bucket);
    }

    const out: DailyDisruption[] = [];

    for (const day of Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day))) {
      const rainAM = this.computeRainHours(day.am.rain, false);
      const rainPM = this.computeRainHours(day.pm.rain, false);

      const snowAM = this.computeSnowHours(day.am.snow);
      const snowPM = this.computeSnowHours(day.pm.snow);

      const windAM = this.computeWindHours(day.am.gust);
      const windPM = this.computeWindHours(day.pm.gust);

      const freezeDay = this.computeFreezeHours(day.am.temp);
      const freezeAM = freezeDay >= 4 ? 4 : 0;
      const freezePM = freezeDay === 8 ? 4 : 0;

      const totalAM = Math.max(rainAM, snowAM, windAM, freezeAM);
      const totalPM = Math.max(rainPM, snowPM, windPM, freezePM);
      const total = Math.min(8, totalAM + totalPM);

      out.push({
        day: day.day,
        rain: Math.min(8, rainAM + rainPM),
        snow: Math.min(8, snowAM + snowPM),
        wind: Math.min(8, windAM + windPM),
        freeze: freezeDay,
        total,
      });
    }

    return out;
  }

  private computeRainHours(values: number[], isTDH: boolean): number {
    if (!values.length) return 0;

    const rainyHours = values.filter((v) => v >= 1).length;
    const maxRain = Math.max(...values, 0);

    // Règle 30 min / 10 mm : non calculable exactement en données horaires.
    // Approximation prudente : si une heure atteint 10 mm, on retient 4h.
    if (maxRain >= 10) return 4;

    if (rainyHours < 1) return 0;

    // Hauteurs 1..3 mm => plafond 3h sauf TDH
    const onlyLowRain = values.some((v) => v >= 1 && v <= 3) && values.every((v) => v <= 3);
    if (onlyLowRain && !isTDH) return Math.min(3, rainyHours);

    return Math.min(4, rainyHours);
  }

  private computeSnowHours(values: number[]): number {
    if (!values.length) return 0;

    // À adapter selon l’unité réelle de snowfall fournie par ton API.
    // Ici on suppose une conversion préalable en cm sur le créneau.
    const totalSnowCm = values.reduce((sum, v) => sum + (Number(v) || 0), 0);
    return totalSnowCm >= 1 ? 4 : 0;
  }

  private computeWindHours(gustValues: number[]): number {
    if (!gustValues.length) return 0;

    const maxGustKmH = Math.max(...gustValues, 0); // si source déjà en km/h
    return maxGustKmH > 60 ? 3 : 0;
  }

  private computeFreezeHours(amTemps: Array<{ hour: number; value: number }>): number {
    if (!amTemps.length) return 0;

    const atOpening =
      amTemps.find((x) => x.hour === this.LEGAL_OPENING_HOUR)?.value ??
      amTemps.slice().sort((a, b) => Math.abs(a.hour - 8) - Math.abs(b.hour - 8))[0]?.value;

    if (atOpening === undefined || atOpening === null || Number.isNaN(atOpening)) return 0;

    if (atOpening <= -2) return 8;
    if (atOpening < 3) return 4;
    return 0;
  }

  private calculateMonthlyDisruptions(): MonthlyDisruption[] {
    const daily = this.calculateWeatherDisruptions();
    const map = new Map<string, MonthlyDisruption>();

    for (const d of daily) {
      const month = d.day.slice(0, 7);
      const bucket = map.get(month) ?? {
        month,
        rain: 0,
        snow: 0,
        wind: 0,
        freeze: 0,
        total: 0,
      };

      bucket.rain += d.rain;
      bucket.snow += d.snow;
      bucket.wind += d.wind;
      bucket.freeze += d.freeze;
      bucket.total += d.total;

      map.set(month, bucket);
    }

    return Array.from(map.values()).sort((a, b) => a.month.localeCompare(b.month));
  }

  // -------------------------
  // Charts Plotly (Historique / Prévision)
  // -------------------------

  private arrayFromMaybeTyped(v: any): number[] {
    if (!v) return [];
    return Array.isArray(v) ? v.map(Number) : Array.from(v as any).map(Number);
  }

  private getSeriesForMetric(key: WeatherMetricKey): number[] {
    if (!this.openMeteoData?.hourly) return [];
    return this.arrayFromMaybeTyped((this.openMeteoData.hourly as any)[key]);
  }

  private splitHistoryForecast(times: Date[], values: number[]) {
    const nowMs = Date.now();
    let split = times.findIndex((t) => t.getTime() >= nowMs);
    if (split < 0) split = times.length;

    const history = {
      x: times.slice(0, split),
      y: values.slice(0, split),
    };

    const maxForecastMs = nowMs + 7 * 24 * 3600 * 1000;
    const fxAll = times.slice(split);
    const fyAll = values.slice(split);

    // Limite à 7 jours
    let fEnd = fxAll.findIndex((t) => t.getTime() > maxForecastMs);
    if (fEnd < 0) fEnd = fxAll.length;

    const forecast = {
      x: fxAll.slice(0, fEnd),
      y: fyAll.slice(0, fEnd),
    };

    return { history, forecast };
  }

  private renderWeatherCharts(): void {
    if (!this.openMeteoData?.hourly) return;

    const times: Date[] = this.openMeteoData.hourly.time ?? [];
    if (!times.length) return;

    const meta = this.selectedMetricMeta;
    const values = this.getSeriesForMetric(meta.key);
    const { history, forecast } = this.splitHistoryForecast(times, values);

    this.renderSingleWeatherChart(
      'plotlyHistory',
      `Historique — ${this.selectedMetricLabel}`,
      history.x,
      history.y,
      meta,
      'history'
    );
    this.renderSingleWeatherChart(
      'plotlyForecast',
      `Prévision — ${this.selectedMetricLabel}`,
      forecast.x,
      forecast.y,
      meta,
      'forecast'
    );
  }

  private renderSingleWeatherChart(
    refName: 'plotlyHistory' | 'plotlyForecast',
    title: string,
    x: Date[],
    y: number[],
    meta: WeatherMetricMeta,
    bucket: 'history' | 'forecast'
  ): void {
    const el = (this.$refs as any)[refName] as HTMLDivElement | undefined;
    if (!el) return;

    const xIso = x.map((d) => d.toISOString());

    const isBar = meta.plotType === 'bar';
    const trace: any = isBar
      ? { x: xIso, y, type: 'bar', name: meta.label, opacity: 0.9 }
      : { x: xIso, y, type: 'scatter', mode: 'lines', name: meta.label };

    const axisCommon: Partial<Plotly.LayoutAxis> = {
      automargin: true,
      showline: true,
      ticks: 'outside',
      tickfont: { color: '#5fc9e4', family: 'Poppins, sans-serif' },
      titlefont: { color: '#5fc9e4', family: 'Poppins, sans-serif' },
      gridcolor: 'rgba(95, 201, 228, 0.15)',
      linecolor: 'rgba(95, 201, 228, 0.35)',
      zerolinecolor: 'rgba(95, 201, 228, 0.15)',
    };

    const layout: Partial<Plotly.Layout> = {
      title: { text: title, font: { color: '#5fc9e4', family: 'Poppins, sans-serif', size: 14 } },
      height: 260,
      margin: { l: 58, r: 16, t: 46, b: 42 },
      hovermode: 'x unified',
      showlegend: false,
      font: { color: '#5fc9e4', family: 'Poppins, sans-serif' },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      xaxis: { ...axisCommon, title: { text: 'Date/heure' } },
      yaxis: { ...axisCommon, title: { text: `${meta.label} (${meta.unit})` } },
      bargap: isBar ? 0.15 : undefined,
    };

    const config: Partial<Plotly.Config> = {
      responsive: true,
      scrollZoom: true,
      displayModeBar: false,
    };

    if (!this.plotlyWeatherReady[bucket]) {
      Plotly.newPlot(el, [trace], layout, config);
      this.plotlyWeatherReady[bucket] = true;
    } else {
      Plotly.react(el, [trace], layout, config);
    }
  }

  private resizeWeatherCharts(): void {
    const h = (this.$refs as any).plotlyHistory as HTMLDivElement | undefined;
    const f = (this.$refs as any).plotlyForecast as HTMLDivElement | undefined;
    if (h) Plotly.Plots.resize(h);
    if (f) Plotly.Plots.resize(f);
  }

  // -------------------------
  // Misc
  // -------------------------

  private get internalBatteryLast(): { dateMs: number; level: number } | null {
    const raw = (this.cameraFresh as any)?.batteryLevels;
    if (!Array.isArray(raw) || raw.length === 0) return null;

    const pts = raw
      .map((x: any) => ({
        dateMs: this.toEpochMs(x?.date),
        level: Number(x?.level ?? x?.value ?? x?.percentage ?? x?.battery),
      }))
      .filter((p: any) => p.dateMs != null && Number.isFinite(p.level)) as any[];

    if (!pts.length) return null;
    return pts.reduce((best, cur) => (cur.dateMs > best.dateMs ? cur : best));
  }

  private get internalBatteryLastAtLabel(): string | null {
    const last = this.internalBatteryLast;
    if (!last) return null;
    return this.formatLocalDateTime(new Date(last.dateMs));
  }

  private get internalBatteryUi() {
    return this.batteryUi(this.internalBatteryLastLevel);
  }

  private getPictoOf(weather: WeatherResult | any): string {
    const base = 'picto-erige ';
    if (!weather || !weather.weather || !weather.weather[0]) return base;

    switch (weather.weather[0].main) {
      case 'Clear':
        return base + 'picto-meteo-soleil';
      case 'Clouds':
        return (
          base +
          (weather.weather[0].icon === '02d' || weather.weather[0].icon === '02n'
            ? 'picto-meteo-mixte'
            : 'picto-meteo-nuage')
        );
      default:
        return base + 'picto-meteo-pluie';
    }
  }

  private async onCollapseShown() {
    // ✅ Plotly météo
    await this.$nextTick();
    if (this.openMeteoData) this.renderWeatherCharts();
    this.resizeWeatherCharts();
    setTimeout(() => this.resizeWeatherCharts(), 200);

    // ✅ Batterie
    (this.$refs.batteryGraphRef as any)?.resizeBatteryGraph?.();

    // ✅ Charge télémétrie sans bloquer l’UI
    if (!this.cameraFresh.isTikee) {
      this.loadTelemetry();
    }
  }

  @Watch('selectedMetric')
  private onSelectedMetricChange() {
    this.$nextTick(() => {
      if (this.openMeteoData) this.renderWeatherCharts();
      this.resizeWeatherCharts();
    });
  }

  private async downloadPDFInt() {
    this.isDownloadingPDF = true;
    try {
      const pdfKey = 'Intemperies.pdf';
      const pdfUrl = await Storage.get(pdfKey, { level: 'public' });
      window.open(pdfUrl as string, '_blank');
    } catch (error) {
      console.error('Erreur lors du téléchargement du PDF:', error);
      this.$bvToast.toast('Erreur lors du téléchargement du PDF.', {
        title: 'Erreur',
        variant: 'danger',
        solid: true,
      });
    } finally {
      this.isDownloadingPDF = false;
    }
  }

  private get telemetryLast(): TelemetryItemDto | null {
    if (!this.telemetryItems.length) return null;
    return this.telemetryItems[this.telemetryItems.length - 1];
  }

  private get internalBatteryLastLevel(): number | null {
    const items = this.telemetryItems ?? [];
    for (let i = items.length - 1; i >= 0; i--) {
      const v = (items[i] as any)?.batteryPercent;
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }

    // fallback shadow
    const s = (this.cameraFresh as any)?.shadow?.reported?.Battery_Percent;
    const ns = Number(s);
    return Number.isFinite(ns) ? ns : null;
  }

  private get externalBatteryLastLevel(): number | null {
    // ✅ 1) prends le dernier point télémétrie qui a externalBatteryPercent défini
    const items = this.telemetryItems ?? [];
    for (let i = items.length - 1; i >= 0; i--) {
      const v = (items[i] as any)?.externalBatteryPercent;
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }

    // ✅ 2) fallback legacy si tu veux (optionnel)
    const raw = (this.cameraFresh as any)?.externalBatteryLevels;
    if (Array.isArray(raw) && raw.length) {
      const pts = raw
        .map((x: any) => ({
          dateMs: this.toEpochMs(x?.date),
          level: Number(x?.level ?? x?.value ?? x?.percentage),
        }))
        .filter((p: any) => p.dateMs != null && Number.isFinite(p.level));

      if (pts.length) {
        pts.sort((a: any, b: any) => a.dateMs - b.dateMs);
        return pts[pts.length - 1].level;
      }
    }

    return null;
  }

  private get dataRate(): number | null {
    // ✅ 1) shadow = instantané (ta source KPI)
    const s = (this.cameraFresh as any)?.shadow?.reported?.Data_Rate;
    const ns = Number(s);
    if (Number.isFinite(ns)) return ns;

    // ✅ 2) fallback telemetry (si shadow absent)
    const t = this.telemetryLast?.dataRate;
    return Number.isFinite(t as any) ? Number(t) : null;
  }

  private get deviceTemperature(): number | null {
    const t = this.telemetryLast?.tempC;
    if (Number.isFinite(t as any)) return Number(t);

    const s = (this.cameraFresh as any)?.shadow?.reported?.Device_Temperature;
    if (s === undefined || s === null || s === '' || s === '--' || s === 'N/A') return null;
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    return n > 100 ? n / 10 : n;
  }

  private async mounted() {
    logger.info('mounted cameraFresh last', {
      id: this.cameraFresh?.id,
      battLast: (this.cameraFresh as any)?.batteryLevels?.slice(-1)?.[0]?.date,
      extLast: (this.cameraFresh as any)?.externalBatteryLevels?.slice(-1)?.[0]?.date,
      tempShadow: (this.cameraFresh as any)?.shadow?.reported?.Device_Temperature,
    });

    this.onLocaleChange();

    if (this.project.id && this.project.city && this.project.city.latlng) {
      this.forecast = Cache.getItem(`forecast-${this.project.id}`);
      if (this.forecast === null) {
        weatherService
          .forecastByCoordinate(this.project.city.latlng.lat, this.project.city.latlng.lng)
          .then((forecastResult: ForecastResult) => {
            this.forecast = forecastResult;
            const expiration = addHours(new Date(), 12);
            Cache.setItem('forecast-' + this.project.id, forecastResult, { expires: expiration.getTime() });
          });
      }
    }

    if (this.project.city && this.project.city.latlng) {
      const lat = Number(this.project.city.latlng.lat);
      const lng = Number(this.project.city.latlng.lng);

      const params = {
        latitude: lat,
        longitude: lng,
        timezone: 'Europe/Paris',
        hourly: [
          'temperature_2m',
          'relative_humidity_2m',
          'rain',
          'snowfall',
          'surface_pressure',
          'wind_speed_10m',
          'wind_gusts_10m',
        ],
        past_days: 92,
        forecast_days: 7,
      };
      const url = 'https://api.open-meteo.com/v1/forecast';

      try {
        const responses = await fetchWeatherApi(url, params);
        if (responses && responses.length > 0) {
          this.handleOpenMeteoResponse(responses[0]);
          // tes calculs existants si tu veux les garder:
          // this.weatherDisruptions = this.calculateWeatherDisruptions();
          // this.monthlyDisruptions = this.calculateMonthlyDisruptions();
        }
      } catch (err) {
        console.error('Erreur OpenMeteo:', err);
      }
    }
  }

  @Watch('locale')
  private onLocaleChange() {
    if (this.project.id && this.project.city && this.project.city.latlng) {
      this.weather = Cache.getItem(`weather-${this.project.id}-${this.locale}`);
      if (this.weather === null) {
        weatherService
          .weatherByCoordinate(this.project.city.latlng.lat, this.project.city.latlng.lng, this.temperatureUnit)
          .then((weatherResult) => {
            this.weather = weatherResult;
            const expiration = addHours(new Date(), 12);
            Cache.setItem(`weather-${this.project.id}-${this.locale}`, weatherResult, {
              expires: expiration.getTime(),
            });
          });
      }
    }
  }
}
